import { get } from "lodash"
import { numberFormatter } from "helpers/formatters/numbers"
import { sanitizePositionSet, isIntradaySimulationEligible } from "../helpers"
import { getIntradayBatchMoment } from "helpers/intraday"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"

function getContentSetExposureSubquery(dateField) {
  return `exposure(contentSetId: $contentSetId) {
        ${dateField}
        factors {
          id
          name
          category
          net
        }
      }`
}

function buildQuery(shouldUseIntradayQuery) {
  if (shouldUseIntradayQuery) {
    // IntradayExposure exposes `timestamp: DateTime!`, not `date` — alias it so downstream select stays uniform
    const exposureSubquery = getContentSetExposureSubquery("date: timestamp")
    return `#multi-date-content-set-exposure
query MultiDateContentSetExposureQuery($modelId: String!, $contentSetId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      ${exposureSubquery}
    }
  }
}`
  }

  const exposureSubquery = getContentSetExposureSubquery("date")
  return `#multi-date-content-set-exposure
query MultiDateContentSetExposureQuery($modelId: String!, $contentSetId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${exposureSubquery}
    }
  }
}`
}

const DAY_BASED_QUERY = buildQuery(false)
const INTRADAY_QUERY = buildQuery(true)

// intradaySimulationBatch returns plain `Exposure` (a `date`, not a `timestamp`) same as the day-based branch.
const BATCH_TREND_QUERY = `#multi-date-content-set-exposure-batch
query MultiDateContentSetExposureBatchQuery($modelId: String!, $contentSetId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $batchId: Id) {
  model(id: $modelId) {
    simulation:intradaySimulationBatch(positionSet: $positionSet, base: $base, on: $on, batchId: $batchId) {
      ${getContentSetExposureSubquery("date")}
    }
  }
}`

function mapContentSetExposureRow(dateData) {
  // A live tick can arrive before the server has finished computing this row's factors.
  return (dateData.factors ?? []).reduce((acc, { id, net }) => {
    acc[id] = net
    return acc
  }, {})
}

// One query per committed batch; the template merges these with the live-tick query (graph/helpers.js extractDatesFromEachSeries).
export function createContentSetExposureIntradayBatchTrendQueries(modelId, contentSetId, positionSet, base, batches) {
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  return batches.map(batch => {
    const key = generateQueryKey("content-set.exposure.multiDate.batch", {
      contentSetId, modelId, positionSet, base, on: batch.date, batchId: batch.id,
    })

    return {
      key,
      skipInvalidation: true,
      query: BATCH_TREND_QUERY,
      variables: { contentSetId, modelId, positionSet: sanitizedPositionSet, base, on: batch.date, batchId: batch.id },
      select: (graphqlResult) => {
        // intradaySimulationBatch is single-instant — one row in, one point out.
        const [row] = get(graphqlResult, "data.model.simulation.exposure", [])
        const batchMoment = getIntradayBatchMoment(batch)
        return {
          values: {
            formatter: numberFormatter,
            // Drop rather than chart a malformed date/asOf — an Invalid Date would corrupt sort order.
            dates: row && batchMoment.isValid() ? [{
              date: batchMoment.toISOString(),
              batchId: batch.id,
              ...mapContentSetExposureRow(row),
            }] : [],
          },
        }
      },
    }
  })
}

export function createContentSetExposureMultiDateQuery(modelId, contentSetId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  const key = generateQueryKey("content-set.exposure.multiDate", {
    contentSetId, modelId, positionSet, base, from, to, intraday: shouldUseIntradaySimulation || undefined,
  })

  const sharedVariables = { contentSetId, modelId, positionSet: sanitizedPositionSet, base }

  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : { ...sharedVariables, from, to }

  return {
    key,
    query: shouldUseIntradaySimulation ? INTRADAY_QUERY : DAY_BASED_QUERY,
    variables,
    select: (graphqlResult) => {
      const exposure = get(graphqlResult, "data.model.simulation.exposure", [])
      return {
        values: {
          formatter: numberFormatter,
          dates: exposure.map(({ date, ...dateData }) => ({
            date,
            ...mapContentSetExposureRow(dateData),
          })),
        },
      }
    },
  }
}
